/**
 * lspParseErrorSignal — pure overlap check between the editor's syntax-error
 * markers (owners `'sql'` — PostgreSQL LSP / mock — AND `'sql-syntax'` — the
 * in-house MSSQL ANTLR∧tree-sitter pass) and the cursor statement's range
 * (VQE Polish, EDG-11..15).
 *
 * The compact affordance's `unavailableReason = 'parse-error'` teaching state
 * (`vqeEditAffordance.ts`) is dormant for real user-typed syntax errors
 * because the VQE's own grammar is deliberately lenient: a broken
 * `… WHERE x > AND (…` still classifies as an editable `select` RenderMode
 * (see the EMPIRICAL GUARD test in `useCursorStatementEditInfoUnavailable.test.tsx`),
 * so `resolveUnavailableReason` never sees a `parse-error` mode to map. There
 * is no LOCAL parse signal to derive the reason from.
 *
 * A language service (`common/components/SqlMonaco.tsx`) flags genuine syntax
 * errors as Monaco markers under `'sql'` (PostgreSQL / mock) or `'sql-syntax'`
 * (MSSQL). This module answers the one question `useCursorStatementEditInfo`
 * needs to override an otherwise-editable statement: does an Error-severity
 * marker under EITHER owner overlap the cursor statement's range? (For the four
 * engines with NO such marker — mysql / oracle / sqlite, and mssql / pgsql when
 * the pass is silent — the LOCAL tree-sitter predicate in
 * `localSyntaxErrorSignal.ts` supplies the same signal.)
 *
 * Deliberately Monaco-free (no `monaco-editor` import) so it's unit-testable
 * without a live editor — the caller (`QueryEditorTabContent.tsx`, which DOES
 * hold a real Monaco instance) normalizes `monaco.editor.IMarker`/`IMarkerData`
 * into the plain `LspSyntaxMarker` shape below before calling in. Line/column
 * fields are 1-based, matching both Monaco's own marker shape and this app's
 * `EditorSelectionRange` — no offset conversion needed, since both inputs
 * already share this representation.
 *
 * Pure, no React, no enums — string-union severity per CLAUDE.md.
 */

/** Monaco's four marker severities, projected to a string union (no enum). */
export type LspSyntaxMarkerSeverity = 'error' | 'warning' | 'info' | 'hint';

/** A single `'sql'`-owner marker, projected to the fields this module needs. */
export type LspSyntaxMarker = {
  readonly severity: LspSyntaxMarkerSeverity;
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
};

/** The cursor statement's range, in the same 1-based line/column shape. */
export type StatementRangeLike = {
  readonly startLineNumber: number;
  readonly startColumn: number;
  readonly endLineNumber: number;
  readonly endColumn: number;
};

type Pos = { readonly line: number; readonly column: number };

function startOf(r: StatementRangeLike): Pos {
  return { line: r.startLineNumber, column: r.startColumn };
}

function endOf(r: StatementRangeLike): Pos {
  return { line: r.endLineNumber, column: r.endColumn };
}

function isBefore(a: Pos, b: Pos): boolean {
  return a.line !== b.line ? a.line < b.line : a.column < b.column;
}

/**
 * True iff range `a` and range `b` share at least one character. Ranges are
 * treated as HALF-OPEN on the (line, column) axis — Monaco's own convention
 * (`endColumn` is exclusive of the last covered character) — so a marker that
 * only TOUCHES a range's edge (its end equals the other's start, or vice
 * versa) does NOT count as overlap.
 */
function rangesOverlap(a: StatementRangeLike, b: StatementRangeLike): boolean {
  return isBefore(startOf(a), endOf(b)) && isBefore(startOf(b), endOf(a));
}

/**
 * True iff at least one Error-severity marker in `markers` overlaps
 * `statementRange`. Warning/info/hint markers never count — a
 * warning-only statement stays editable. `false` when there's no statement
 * range (nothing to check against) or no markers at all. Owner-agnostic: the
 * caller passes the union of `'sql'` + `'sql-syntax'` Error markers.
 */
export function hasOverlappingSyntaxError(
  markers: readonly LspSyntaxMarker[],
  statementRange: StatementRangeLike | null,
): boolean {
  if (!statementRange || markers.length === 0) return false;
  return markers.some(
    (marker) => marker.severity === 'error' && rangesOverlap(marker, statementRange),
  );
}
